import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/api/axios';
import { useAuth } from './AuthContext';

const SchemeContext = createContext(null);

export const SchemeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Initialize from localStorage or fallback to ID 1
  const [selectedSchemeId, setSelectedSchemeId] = useState(() => {
    const saved = localStorage.getItem('last_selected_scheme');
    return saved ? parseInt(saved) : 1;
  });

  // The query is now dependent on isAuthenticated
  const { data: schemes = [], isLoading: schemesLoading, refetch } = useQuery({
    queryKey: ['schemes', isAuthenticated], // Added isAuthenticated to key
    queryFn: async () => {
      const res = await api.get('get_schemes.php');
      const raw = res.data?.data || [];
      return raw.map(s => ({
        ...s,
        name: s.scheme_name || `Scheme #${s.id || 'Unknown'}`
      }));
    },
    // Only fetch if authenticated
    enabled: isAuthenticated,
    staleTime: 300000,
  });

  // When authentication state changes to true, invalidate and refetch
  useEffect(() => {
    if (isAuthenticated) {
      queryClient.invalidateQueries({ queryKey: ['schemes'] });
    }
  }, [isAuthenticated, queryClient]);

  const selectScheme = useCallback((id) => {
    setSelectedSchemeId(id);
    localStorage.setItem('last_selected_scheme', id);
  }, []);

  const selectedScheme = schemes.find(s => s.id === selectedSchemeId) || null;

  return (
    <SchemeContext.Provider value={{ 
      schemes, 
      schemesLoading, 
      selectedSchemeId, 
      selectedScheme, 
      selectScheme 
    }}>
      {children}
    </SchemeContext.Provider>
  );
};

export const useScheme = () => useContext(SchemeContext);